import { useEffect, useMemo, useRef, useState } from "react";

// Lightweight searchable dropdown used across the Sell Your Car form.
// Full control over styling + RTL behaviour, no extra dependencies.
export default function SearchSelect({
  value,
  onChange,
  options,
  placeholder,
  testid,
  disabled = false,
  emptyLabel = "No matches",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => String(o.label).toLowerCase().includes(q) || String(o.value).toLowerCase().includes(q));
  }, [options, query]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setActive(0);
      const t = setTimeout(() => inputRef.current && inputRef.current.focus(), 30);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open]);

  useEffect(() => { setActive(0); }, [query]);

  const selected = options.find((o) => o.value === value);
  const pick = (opt) => { onChange(opt.value); setOpen(false); };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, Math.max(filtered.length - 1, 0))); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (filtered[active]) pick(filtered[active]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={wrapRef} className="relative" data-testid={`select-${testid}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="listbox"
        className={`field-input flex items-center justify-between gap-3 cursor-pointer ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
        style={{ textAlign: "start" }}
        data-testid={`select-button-${testid}`}
      >
        <span className={`truncate ${selected ? "" : "text-[#666663]"}`}>{selected ? selected.label : placeholder}</span>
        <span className="text-[#999995] text-[9px]" aria-hidden="true">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div
          className="absolute z-30 left-0 right-0 mt-1 bg-[#090A0B] border border-white/15 shadow-2xl"
          data-testid={`select-panel-${testid}`}
        >
          <div className="p-2 border-b border-white/10">
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              className="field-input !py-2"
              placeholder=" " /* searchable */
              aria-label="Search options"
              data-testid={`select-input-${testid}`}
            />
          </div>
          <ul className="max-h-56 overflow-y-auto" role="listbox">
            {filtered.length === 0 && (
              <li className="px-4 py-3 mono text-[11px] text-[#666663] uppercase tracking-[0.14em]" data-testid={`select-empty-${testid}`}>
                {emptyLabel}
              </li>
            )}
            {filtered.map((o, i) => (
              <li
                key={`${o.code || ""}${o.value}-${i}`}
                role="option"
                aria-selected={o.value === value}
                onMouseEnter={() => setActive(i)}
                onClick={() => pick(o)}
                data-testid={`select-option-${testid}-${String(o.value).replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                className={`px-4 py-2.5 text-[13px] cursor-pointer transition-colors duration-150 ${
                  i === active ? "bg-white/[0.06] text-[#F3F3F0]" : "text-[#999995]"
                } ${o.value === value ? "text-[#F3F3F0]" : ""}`}
              >
                {o.label}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
